/**
 * Mộc By Dylan — Inventory Auth
 * ─────────────────────────────
 * Email-based access gate for the internal inventory page.
 * No session management, no cookies, no OAuth.
 *
 * Page-load flow:
 *   1. Check localStorage for 'inventory_staff_email'.
 *   2. Found  → re-validate via POST /api/internal/auth/validate
 *              → success: show dashboard (dispatch 'inv:auth-success')
 *              → fail:    clear storage, show gate
 *   3. Missing → show gate
 *
 * Form submit flow:
 *   1. POST /api/internal/auth/validate { email }
 *   2. { success: true }  → store email, dispatch 'inv:auth-success'
 *   3. { success: false } → show error, stay on gate
 *
 * Events dispatched on `document`:
 *   'inv:auth-success' — email confirmed, detail: { email }
 *   'inv:auth-clear'   — logged out
 *
 * Public API:  window.InventoryAuth = { getStoredEmail, logout }
 */

'use strict';

/* ── Config ────────────────────────────────────────────────── */
const AUTH = {
  endpoint:   'https://mocbydylan-payment-production.up.railway.app/api/internal/auth/validate',
  storageKey: 'inventory_staff_email',
};

/* ── Public helpers ────────────────────────────────────────── */

/**
 * Return the staff email from localStorage, or null.
 * @returns {string|null}
 */
function getStoredEmail() {
  try { return localStorage.getItem(AUTH.storageKey); }
  catch { return null; }
}

/**
 * Validate an email against the auth API.
 * @param {string} email
 * @returns {Promise<boolean>}
 */
async function validateEmail(email) {
  try {
    const res = await fetch(AUTH.endpoint, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Staff-Email': email,
      },
      body:    JSON.stringify({ email }),
    });
    console.debug('[InventoryAuth] validate response status:', res.status);
    if (!res.ok) {
      console.warn('[InventoryAuth] validate failed — HTTP', res.status);
      return false;
    }
    const data = await res.json();
    console.debug('[InventoryAuth] validate response body:', data);
    const ok = data.success === true;
    console.debug('[InventoryAuth] auth result:', ok);
    return ok;
  } catch (err) {
    console.error('[InventoryAuth] validate error:', err);
    return false;
  }
}

/**
 * Remove stored email and return user to the email gate.
 */
function logout() {
  _clearStorage();
  _showGate();
  _hideDashboard();
  document.dispatchEvent(new CustomEvent('inv:auth-clear'));
}

/* ── Init ──────────────────────────────────────────────────── */

async function initAuth() {
  const gate      = document.getElementById('inv-gate');
  const dashboard = document.getElementById('inv-dashboard');
  if (!gate || !dashboard) return;

  const stored = getStoredEmail();

  if (stored) {
    /* Auto-validate persisted email */
    _setGateSpinning(true);
    _showGate();                     /* show gate briefly while validating */

    const ok = await validateEmail(stored);
    _setGateSpinning(false);

    if (ok) {
      _onSuccess(stored);
    } else {
      _clearStorage();
      _showGate();
    }
  } else {
    _showGate();
  }

  _bindForm();
  _bindLogout();
}

/* ── Form handling ─────────────────────────────────────────── */

function _bindForm() {
  const form = document.getElementById('inv-gate-form');
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const input  = document.getElementById('inv-gate-email');
    const errEl  = document.getElementById('inv-gate-error');
    const email  = (input?.value ?? '').trim();

    _clearError(errEl);

    if (!email) {
      _showError(errEl, 'Please enter your email address.');
      input?.focus();
      return;
    }

    if (!_isEmail(email)) {
      _showError(errEl, 'Please enter a valid email address.');
      input?.focus();
      return;
    }

    _setSubmitting(true);
    const ok = await validateEmail(email);
    _setSubmitting(false);

    if (ok) {
      _storeEmail(email);
      _onSuccess(email);
    } else {
      _showError(errEl, 'You do not have permission to access this page.');
      input?.focus();
    }
  });

  /* Clear error on re-type */
  document.getElementById('inv-gate-email')?.addEventListener('input', () => {
    _clearError(document.getElementById('inv-gate-error'));
  });
}

function _bindLogout() {
  /* Delegated — button lives inside the lazily-shown dashboard */
  document.addEventListener('click', (e) => {
    if (e.target.closest('#inv-logout')) logout();
  });
}

/* ── Success handler ───────────────────────────────────────── */

function _onSuccess(email) {
  console.debug('[InventoryAuth] auth success — showing dashboard for:', email);

  /* Populate staff email display */
  const el = document.getElementById('inv-staff-email');
  if (el) el.textContent = email;

  _hideGate();
  _showDashboard();

  const gate = document.getElementById('inv-gate');
  const dash = document.getElementById('inv-dashboard');
  console.debug('[InventoryAuth] inv-gate hidden:', gate?.hidden, '| inv-dashboard hidden:', dash?.hidden);

  document.dispatchEvent(
    new CustomEvent('inv:auth-success', { detail: { email } })
  );
}

/* ── DOM helpers ───────────────────────────────────────────── */

function _showGate()      { const el = document.getElementById('inv-gate');      if (el) el.hidden = false; }
function _hideGate()      { const el = document.getElementById('inv-gate');      if (el) el.hidden = true;  }
function _showDashboard() { const el = document.getElementById('inv-dashboard'); if (el) el.hidden = false; }
function _hideDashboard() { const el = document.getElementById('inv-dashboard'); if (el) el.hidden = true;  }

function _setGateSpinning(on) {
  const card = document.querySelector('.inv-gate__card');
  if (card) card.style.opacity = on ? '0.55' : '';
}

function _setSubmitting(on) {
  const btn = document.getElementById('inv-gate-submit');
  if (!btn) return;
  btn.disabled = on;
  btn.classList.toggle('inv-gate__btn--loading', on);
  if (!on) btn.textContent = 'Continue';
}

function _showError(el, msg) {
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
}

function _clearError(el) {
  if (!el) return;
  el.textContent = '';
  el.hidden = true;
}

/* ── Storage ───────────────────────────────────────────────── */

function _storeEmail(email) {
  try { localStorage.setItem(AUTH.storageKey, email); } catch { /* noop */ }
}

function _clearStorage() {
  try { localStorage.removeItem(AUTH.storageKey); } catch { /* noop */ }
}

/* ── Utility ───────────────────────────────────────────────── */

function _isEmail(str) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

/* ── Boot + expose ─────────────────────────────────────────── */

window.InventoryAuth = { getStoredEmail, validateEmail, logout };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAuth);
} else {
  initAuth();
}
